import { DbPagination } from '../../models/generic/pagination';

export interface ResponseDocumentOrderBy {
  field: string;
  direction: 'asc' | 'desc';
}

export interface PostResponseProjectDocumentsRequestBody {
  fields: string[];
  filters: Record<string, unknown>;
  limit: number;
  offset: number;
  paginate?: boolean;
  order_by?: ResponseDocumentOrderBy[];
}

/** Project using a document, returned by the search enrichment (`response_projects` / `projects`). */
export interface DbProjectRef {
  id: number;
  name: string;
  is_active?: boolean;
}

export interface DbDocumentUploader {
  id: number;
  name: string;
}

/** Written by the knowledge base indexer, and only ever set on internal documents. */
export type DocumentIndexationStatus = 'pending' | 'success' | 'failure' | 'not_indexable';

export interface DbResponseProjectDocument {
  id: number;
  uid: string;
  name: string;
  custom_name: string | null;
  file_size: number;
  s3_key: string;
  response_document_subtype_id: number;
  status?: string;
  indexation_status?: DocumentIndexationStatus | null;
  is_in_library?: boolean;
  response_projects?: DbProjectRef[];
  uploader?: DbDocumentUploader | null;
  created_at?: string;
  updated_at?: string;
  download_presigned_url?: string | null;
}

export interface ResponseProjectDocumentsApiResponse {
  data: DbResponseProjectDocument[];
  pagination?: DbPagination;
}

export interface ResponseProjectDocument {
  id: string;
  uid: string;
  name: string;
  customName: string | null;
  fileSize?: number;
  s3Key: string;
  responseDocumentSubtypeId: number;
  status?: string;
  indexationStatus?: DocumentIndexationStatus | null;
  isInLibrary?: boolean;
  projects?: DbProjectRef[];
  uploader?: { id: number; name: string } | null;
  createdAt?: string;
  updatedAt?: string;
  downloadPresignedUrl?: string | null;
}

export function isDocumentIndexationPending(doc: Pick<ResponseProjectDocument, 'status' | 'indexationStatus'>): boolean {
  return doc.status === 'uploaded' && doc.indexationStatus === 'pending';
}

export type LibrarySortField = 'name' | 'used' | 'recent';

export const LIBRARY_SORT_ORDER_BY: Record<LibrarySortField, ResponseDocumentOrderBy[]> = {
  name: [{ field: 'name', direction: 'asc' }],
  used: [{ field: 'response_projects_count', direction: 'desc' }],
  recent: [{ field: 'updated_at', direction: 'desc' }],
};

export const LIBRARY_SORT_DROPDOWN_ITEMS: { label: string; value: LibrarySortField; direction: 'asc' | 'desc' }[] = [
  { label: 'tenders.library.sort.name', value: 'name', direction: 'asc' },
  { label: 'tenders.library.sort.used', value: 'used', direction: 'desc' },
  { label: 'tenders.library.sort.recent', value: 'recent', direction: 'desc' },
];

export const LIBRARY_DOCUMENT_FIELDS = [
  'id',
  'uid',
  'name',
  'custom_name',
  'response_document_subtype_id',
  'is_in_library',
  'response_projects',
  'uploader',
  'updated_at',
  'status',
  'indexation_status',
] as const;

export const LIBRARY_PAGE_SIZE = 100;

export const MAX_REFERENCE_DOCUMENTS = 50;

export function buildLibraryFilters(query: string, subtypeIds: number[] = []): Record<string, unknown> {
  const filters: Record<string, unknown> = { is_in_library: true };
  const q = query.trim();
  if (q) filters['name'] = `%${q}%`;
  if (subtypeIds.length === 1) filters['response_document_subtype_id'] = subtypeIds[0];
  else if (subtypeIds.length > 1) filters['response_document_subtype_id'] = subtypeIds;
  return filters;
}

export interface ResponseProjectUploadFile {
  uid: string;
  name: string;
  size?: number;
  sourceFile?: File;
  subtype?: string;
  subtypeId?: number;
  customName?: string | null;
  documentId?: number;
  uploadProgress?: number;
  processingStep?: DocumentProcessingStepEnum;
  justCategorized?: boolean;
  hidden?: boolean;
  replaceId?: number;
  isInLibrary?: boolean;
}

export function isArchiveFile(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.endsWith('.zip') || lower.endsWith('.rar');
}

export enum DocumentProcessingStepEnum {
  IMPORTING = 'IMPORTING',
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
  EXTRACTING = 'EXTRACTING',
  CATEGORIZING = 'CATEGORIZING',
  CATEGORIZED = 'CATEGORIZED',
  INDEXING = 'INDEXING',
  UNCATEGORIZED = 'UNCATEGORIZED',
  ERROR = 'ERROR',
}
